import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Bot, Flame, CalendarRange, Drama, MicVocal, Dna, Gauge, Radar,
  MessageCircleHeart, Sparkles, Loader2, Copy, ArrowRight, CheckCircle2,
  TrendingUp, Lock, Zap,
} from 'lucide-react';
import { SmartShare, DestinationsManager } from '../components/SmartShare';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KFN = 'Kari Future Now Creations™';

const DestinationsContext = createContext({ destinations: [], reload: () => {} });
const useDestinations = () => useContext(DestinationsContext);

const TABS = [
  { id: 'viral-pack', label: 'Viral Pack', icon: Flame, accent: 'from-orange-500 to-red-600' },
  { id: 'burst', label: 'Burst Calendar', icon: CalendarRange, accent: 'from-amber-500 to-orange-600' },
  { id: 'persona', label: 'Persona Rewriter', icon: Drama, accent: 'from-pink-500 to-red-500' },
  { id: 'hooks', label: 'Scroll-Stopper', icon: MicVocal, accent: 'from-yellow-500 to-orange-500' },
  { id: 'dna', label: 'Brand DNA', icon: Dna, accent: 'from-emerald-500 to-teal-500' },
  { id: 'predict', label: 'Engagement Oracle', icon: Gauge, accent: 'from-fuchsia-500 to-purple-600' },
  { id: 'trends', label: 'Trend Radar', icon: Radar, accent: 'from-cyan-500 to-blue-500' },
  { id: 'comments', label: 'Comment Killer', icon: MessageCircleHeart, accent: 'from-rose-500 to-pink-600' },
  { id: 'destinations', label: 'Destinations', icon: Zap, accent: 'from-orange-500 to-amber-500' },
];

const copyText = (t) => {
  navigator.clipboard.writeText(t);
  toast.success('Copied to clipboard');
};

const KFNStamp = ({ className = '' }) => (
  <div className={`flex items-center gap-1.5 text-[11px] text-orange-300/80 ${className}`}>
    <Sparkles className="w-3 h-3" /> Made with {KFN}
  </div>
);

const AILab = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [activeTab, setActiveTab] = useState('viral-pack');

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(me.data);
        try {
          const sub = await axios.get(`${BACKEND_URL}/api/subscriptions/status`, { withCredentials: true });
          setHasSubscription(!!sub.data?.has_subscription);
        } catch {
          setHasSubscription(false);
        }
      } catch {
        navigate('/login');
      } finally {
        setLoadingAuth(false);
      }
    })();
  }, [navigate]);

  const startSubscription = async () => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/subscriptions/create`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.success("You're already subscribed!");
        setHasSubscription(true);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate('/login?redirect=/ai-lab&action=subscribe');
        return;
      }
      toast.error('Could not start subscription');
    }
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-orange-400 animate-spin" />
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] text-white pt-24 pb-20 relative overflow-hidden" data-testid="ailab-paywall">
        <div className="pointer-events-none absolute top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-orange-500/20 blur-[140px]" />
        <div className="pointer-events-none absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full bg-red-600/20 blur-[160px]" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/15">
              <Lock className="w-3 h-3 mr-1.5" /> Subscribers Only
            </Badge>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Unlock the AI Lab</span>
            </h1>
            <p className="text-white/70 mt-3">
              8 super-duper AI tools nobody else offers. Only ${'\u00A0'}<span className="text-white font-bold">$29.99/mo</span>.
            </p>
            <KFNStamp className="justify-center mt-3" />
          </div>
          <Card className="bg-white/5 border border-white/10 backdrop-blur-md">
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-2 gap-3">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <div key={t.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${t.accent} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">{t.label}</div>
                        <div className="text-xs text-white/60">Unlimited usage</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button
                onClick={startSubscription}
                size="lg"
                className="w-full mt-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-6 shadow-xl shadow-orange-500/30"
                data-testid="lab-paywall-subscribe"
              >
                <Zap className="w-5 h-5 mr-2" /> Subscribe — $29.99/mo
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] text-white pt-24 pb-20 relative overflow-hidden" data-testid="ailab-page">
      <div className="pointer-events-none absolute top-20 -left-24 w-96 h-96 rounded-full bg-orange-500/15 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-24 w-[28rem] h-[28rem] rounded-full bg-red-600/15 blur-[140px]" />

      <DestinationsProvider>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-semibold mb-4">
            <Bot className="w-3.5 h-3.5" /> KARI FUTURE NOW CREATIONS™ — AI LAB
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-red-500 bg-clip-text text-transparent">
              The 9 AI Superpowers
            </span>
            <br />
            <span className="text-white">No Other App Has.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 mt-3 max-w-2xl">
            Welcome back, {user?.name}. Pick a tool below — every output is signed{' '}
            <span className="text-orange-300 font-semibold">{KFN}</span>.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 border border-white/10 backdrop-blur-md p-1 h-auto flex-wrap gap-1 mb-6">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  data-testid={`tab-${t.id}`}
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white text-white/70 hover:text-white px-3 py-2 text-xs md:text-sm font-medium gap-2"
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="viral-pack"><ViralPackTool /></TabsContent>
          <TabsContent value="burst"><BurstCalendarTool /></TabsContent>
          <TabsContent value="persona"><PersonaTool /></TabsContent>
          <TabsContent value="hooks"><ScrollStopperTool /></TabsContent>
          <TabsContent value="dna"><BrandDnaTool /></TabsContent>
          <TabsContent value="predict"><EngagementTool /></TabsContent>
          <TabsContent value="trends"><TrendRadarTool /></TabsContent>
          <TabsContent value="comments"><CommentKillerTool /></TabsContent>
          <TabsContent value="destinations"><DestinationsTab /></TabsContent>
        </Tabs>

        <div className="mt-10 text-center">
          <KFNStamp className="justify-center" />
        </div>
      </div>
      </DestinationsProvider>
    </div>
  );
};

// ============== Destinations Context Provider ==============
const DestinationsProvider = ({ children }) => {
  const [destinations, setDestinations] = useState([]);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/social/destinations`, { withCredentials: true });
      setDestinations(Array.isArray(data) ? data : []);
    } catch {
      setDestinations([]);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <DestinationsContext.Provider value={{ destinations, reload }}>
      {children}
    </DestinationsContext.Provider>
  );
};

// ============== Destinations Tab ==============
const DestinationsTab = () => {
  const { destinations, reload } = useDestinations();
  return (
    <ToolShell icon={Zap} title="Smart Destinations" subtitle="Post WITHOUT a Facebook App or secret key. Connect Buffer, Publer, Zapier, or use one-tap deep links." accent="from-orange-500 to-amber-500">
      <DestinationsManager destinations={destinations} onChange={reload} />
    </ToolShell>
  );
};

// ============== Reusable shell ==============
const ToolShell = ({ icon: Icon, title, subtitle, children, accent = 'from-orange-500 to-red-600' }) => (
  <Card className="bg-white/5 border border-white/10 backdrop-blur-md" data-testid={`tool-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardHeader>
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg shadow-orange-500/30`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <CardTitle className="text-xl text-white">{title}</CardTitle>
          <CardDescription className="text-white/60">{subtitle}</CardDescription>
        </div>
        <Badge className="ml-auto bg-orange-500/10 border border-orange-500/30 text-orange-300">
          {KFN}
        </Badge>
      </div>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const RunButton = ({ onClick, loading, children, testid }) => (
  <Button
    onClick={onClick}
    disabled={loading}
    size="lg"
    data-testid={testid}
    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-5 shadow-lg shadow-orange-500/30"
  >
    {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Working...</>) : (<><Sparkles className="w-5 h-5 mr-2" /> {children}</>)}
  </Button>
);

const ResultBox = ({ children }) => (
  <div className="mt-5 rounded-xl bg-black/40 border border-white/10 p-4 space-y-3">{children}</div>
);

// ============== Tool 1: Viral Pack ==============
const ViralPackTool = () => {
  const { destinations, reload } = useDestinations();
  const [prompt, setPrompt] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('hype');
  const [useDna, setUseDna] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!prompt.trim()) return toast.error('Add a topic first');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/viral-pack`,
        { prompt, platform, tone, use_brand_dna: useDna }, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <ToolShell icon={Flame} title="Viral Pack" subtitle="One topic → 5 viral variants + engagement scores + best hashtags + best post time." accent="from-orange-500 to-red-600">
      <Textarea data-testid="viral-prompt" placeholder="Topic: e.g. how to wake up at 5am without coffee" value={prompt} onChange={(e) => setPrompt(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3" rows={3} />
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
        <select value={tone} onChange={(e) => setTone(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value="hype">Hype Mogul</option>
          <option value="authority">Calm Authority</option>
          <option value="bestie">Bestie Vibe</option>
          <option value="luxury">Luxury Whisper</option>
          <option value="casual">Casual</option>
          <option value="professional">Professional</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-white/70 px-2">
          <input type="checkbox" checked={useDna} onChange={(e) => setUseDna(e.target.checked)} /> Use my Brand DNA
        </label>
      </div>
      <RunButton onClick={run} loading={loading} testid="viral-run">Generate Viral Pack</RunButton>

      {data?.variants?.length > 0 && (
        <ResultBox>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-white/70">
              Best time to post: <span className="text-orange-300 font-semibold">{data.best_time_to_post_local || '—'}</span>
            </div>
            <KFNStamp />
          </div>
          {data.variants.map((v) => (
            <div key={v.id} className={`rounded-lg border p-3 ${v.id === data.winner_id ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <Badge className="bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px]">
                  #{v.id} • {v.hook_style}
                </Badge>
                <div className="flex items-center gap-2">
                  {v.id === data.winner_id && <Badge className="bg-emerald-500 text-white">🏆 Winner</Badge>}
                  <span className="text-sm font-bold text-orange-300">{v.engagement_score}/100</span>
                </div>
              </div>
              <div className="text-sm text-white/90 whitespace-pre-wrap">{v.text}</div>
              <div className="flex items-center justify-between mt-2 text-xs text-white/50">
                <span>~{v.predicted_likes_min}-{v.predicted_likes_max} likes • {v.why_it_works}</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => copyText(v.text)} className="h-7 text-orange-300 hover:bg-orange-500/10"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                  <SmartShare text={v.text} destinations={destinations} onReloadDestinations={reload} />
                </div>
              </div>
            </div>
          ))}
          {data.hashtags?.length > 0 && (
            <div className="pt-2">
              <div className="text-xs text-white/60 mb-1">Best hashtags (ranked)</div>
              <div className="flex flex-wrap gap-1.5">
                {data.hashtags.map((h) => (
                  <span key={h} className="text-xs px-2 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-200">{h}</span>
                ))}
              </div>
            </div>
          )}
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 2: Burst Calendar ==============
const BurstCalendarTool = () => {
  const [niche, setNiche] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('casual');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!niche.trim()) return toast.error('Tell us your niche');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/burst-calendar`,
        { niche, platform, tone, days }, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <ToolShell icon={CalendarRange} title="Burst Calendar" subtitle="30 days of content in 60 seconds. A whole month of posts with one click." accent="from-amber-500 to-orange-600">
      <Input data-testid="burst-niche" placeholder="Niche (e.g. fitness coach for busy moms)" value={niche} onChange={(e) => setNiche(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3" />
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
        <select value={tone} onChange={(e) => setTone(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value="casual">Casual</option>
          <option value="professional">Professional</option>
          <option value="hype">Hype Mogul</option>
          <option value="bestie">Bestie Vibe</option>
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
        </select>
      </div>
      <RunButton onClick={run} loading={loading} testid="burst-run">Build {days}-Day Calendar</RunButton>

      {data?.days?.length > 0 && (
        <ResultBox>
          <div className="grid md:grid-cols-2 gap-3">
            {data.days.map((d) => (
              <div key={d.day} className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between mb-1">
                  <Badge className="bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px]">Day {d.day} • {d.format}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => copyText(d.post)} className="h-7 text-orange-300 hover:bg-orange-500/10"><Copy className="w-3 h-3" /></Button>
                </div>
                <div className="text-sm font-semibold text-white mb-1">{d.theme}</div>
                <div className="text-xs text-orange-300 italic mb-1">"{d.hook}"</div>
                <div className="text-xs text-white/80 whitespace-pre-wrap">{d.post}</div>
                {d.cta && <div className="text-[11px] text-emerald-400 mt-1">CTA: {d.cta}</div>}
              </div>
            ))}
          </div>
          <KFNStamp className="pt-2" />
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 3: Persona Rewriter ==============
const PersonaTool = () => {
  const { destinations, reload } = useDestinations();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!text.trim()) return toast.error('Paste a post first');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/persona-rewriter`, { text }, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <ToolShell icon={Drama} title="Persona Rewriter" subtitle="Same post → 4 distinct brand voices. Hype, Authority, Bestie, Luxury." accent="from-pink-500 to-red-500">
      <Textarea data-testid="persona-text" placeholder="Paste your post idea here..." value={text} onChange={(e) => setText(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3" rows={5} />
      <RunButton onClick={run} loading={loading} testid="persona-run">Rewrite in 4 Voices</RunButton>

      {data?.personas?.length > 0 && (
        <ResultBox>
          <div className="grid md:grid-cols-2 gap-3">
            {data.personas.map((p) => (
              <div key={p.name} className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div>
                    <div className="font-bold text-white text-sm">{p.name}</div>
                    <div className="text-xs text-white/50">{p.vibe}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => copyText(p.rewrite)} className="h-7 text-orange-300 hover:bg-orange-500/10"><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="text-sm text-white/90 whitespace-pre-wrap mb-2">{p.rewrite}</div>
                <div className="flex justify-end">
                  <SmartShare text={p.rewrite} destinations={destinations} onReloadDestinations={reload} />
                </div>
              </div>
            ))}
          </div>
          <KFNStamp className="pt-2" />
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 4: Scroll Stopper ==============
const ScrollStopperTool = () => {
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!topic.trim()) return toast.error('Add a topic');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/scroll-stopper`, { topic, count }, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <ToolShell icon={MicVocal} title="Scroll-Stopper" subtitle="First-line hooks ONLY. The 0.4-second decision that stops the scroll." accent="from-yellow-500 to-orange-500">
      <Input data-testid="hook-topic" placeholder="Topic (e.g. AI side hustles in 2026)" value={topic} onChange={(e) => setTopic(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3" />
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value={5}>5 hooks</option>
          <option value={10}>10 hooks</option>
          <option value={15}>15 hooks</option>
        </select>
      </div>
      <RunButton onClick={run} loading={loading} testid="hook-run">Generate Hooks</RunButton>

      {data?.hooks?.length > 0 && (
        <ResultBox>
          <div className="space-y-2">
            {data.hooks.sort((a, b) => (b.scroll_stop_score || 0) - (a.scroll_stop_score || 0)).map((h) => (
              <div key={h.id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center font-black text-orange-300">
                  {h.scroll_stop_score}
                </div>
                <div className="flex-1">
                  <Badge className="bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] mb-1">{h.pattern}</Badge>
                  <div className="text-sm text-white/90">{h.hook}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copyText(h.hook)} className="text-orange-300 hover:bg-orange-500/10"><Copy className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
          <KFNStamp className="pt-2" />
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 5: Brand DNA ==============
const BrandDnaTool = () => {
  const [samples, setSamples] = useState(['', '', '']);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/ai/brand-dna`, { withCredentials: true })
      .then(({ data }) => setExisting(data && data.dna_summary ? data : null))
      .catch(() => {});
  }, []);

  const run = async () => {
    const filled = samples.map(s => s.trim()).filter(Boolean);
    if (filled.length < 1) return toast.error('Paste at least 1 past post');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/brand-dna`, { samples: filled }, { withCredentials: true });
      setData(data); setExisting(data);
      toast.success('Brand DNA saved! Other tools will now match your voice.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const dnaToShow = data || existing;

  return (
    <ToolShell icon={Dna} title="Brand DNA Engine" subtitle="Train AI on YOUR voice. Paste 3 past posts → AI clones your tone forever." accent="from-emerald-500 to-teal-500">
      {existing && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 mb-4 text-sm">
          <div className="text-emerald-300 font-semibold mb-1">✓ Brand DNA active</div>
          <div className="text-white/70 text-xs">{existing.dna_summary}</div>
        </div>
      )}
      <div className="space-y-3 mb-4">
        {samples.map((s, i) => (
          <Textarea
            key={`sample-${i}`}
            data-testid={`dna-sample-${i}`}
            placeholder={`Past post #${i + 1} — paste your own writing here...`}
            value={s}
            onChange={(e) => {
              const next = [...samples]; next[i] = e.target.value; setSamples(next);
            }}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            rows={3}
          />
        ))}
      </div>
      <RunButton onClick={run} loading={loading} testid="dna-run">Extract My Brand DNA</RunButton>

      {dnaToShow?.dna_summary && (
        <ResultBox>
          <div className="text-sm text-white/85"><span className="text-emerald-400 font-bold">Voice fingerprint: </span>{dnaToShow.dna_summary}</div>
          {dnaToShow.tone_keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {dnaToShow.tone_keywords.map((k) => (
                <span key={k} className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">{k}</span>
              ))}
            </div>
          )}
          {dnaToShow.vocab_signatures?.length > 0 && (
            <div className="text-xs text-white/60">
              <span className="text-white/80 font-semibold">Your signature words: </span>
              {dnaToShow.vocab_signatures.join(', ')}
            </div>
          )}
          <KFNStamp />
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 6: Engagement Predictor ==============
const EngagementTool = () => {
  const [text, setText] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!text.trim()) return toast.error('Paste a draft post');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/engagement-predict`, { text, platform }, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const score = data?.engagement_score ?? 0;
  const scoreColor = score >= 80 ? 'text-emerald-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400';

  return (
    <ToolShell icon={Gauge} title="Engagement Oracle" subtitle="Score your post BEFORE you publish. Predicts likes, shares, scroll-stop strength + 3 ruthless upgrades." accent="from-fuchsia-500 to-purple-600">
      <Textarea data-testid="predict-text" placeholder="Paste a draft post you're about to publish..." value={text} onChange={(e) => setText(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3" rows={5} />
      <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5 mb-4 w-full">
        <option value="instagram">Instagram</option>
        <option value="facebook">Facebook</option>
      </select>
      <RunButton onClick={run} loading={loading} testid="predict-run">Score My Post</RunButton>

      {data?.engagement_score !== undefined && (
        <ResultBox>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-white/5 border border-white/10 p-4 text-center">
              <div className={`text-5xl font-black ${scoreColor}`}>{score}</div>
              <div className="text-xs text-white/60 mt-1">Engagement Score</div>
              <Badge className="mt-2 bg-orange-500/15 border border-orange-500/30 text-orange-300">Grade: {data.grade}</Badge>
            </div>
            <div className="rounded-lg bg-white/5 border border-white/10 p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-white/60">Predicted likes</span><span className="font-bold text-white">{data.predicted_likes_range}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/60">Predicted shares</span><span className="font-bold text-white">{data.predicted_shares_range}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/60">Save rate</span><span className="font-bold text-white">{data.predicted_save_rate_pct}%</span></div>
            </div>
            <div className="rounded-lg bg-white/5 border border-white/10 p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-white/60">Scroll-stop</span><span className="font-bold text-white">{data.scroll_stop_strength}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/60">First-line</span><span className="font-bold text-white">{data.first_line_quality}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/60">CTA</span><span className="font-bold text-white">{data.cta_strength}</span></div>
            </div>
          </div>
          {data.verdict && (
            <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-3 text-sm text-orange-200">
              <span className="font-bold">Verdict: </span>{data.verdict}
            </div>
          )}
          {data.upgrades?.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm text-white/80 font-semibold">Concrete upgrades:</div>
              {data.upgrades.map((u, i) => (
                <div key={`up-${i}`} className="rounded-lg bg-white/5 border border-white/10 p-3 text-sm">
                  <div className="text-red-300 text-xs mb-1">Issue: {u.issue}</div>
                  <div className="text-emerald-300">→ {u.rewrite}</div>
                </div>
              ))}
            </div>
          )}
          <KFNStamp />
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 7: Trend Radar ==============
const TrendRadarTool = () => {
  const [niche, setNiche] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!niche.trim()) return toast.error('Enter your niche');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/trend-radar`, { niche, platform }, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <ToolShell icon={Radar} title="Trend Radar" subtitle="The 5 hottest content angles in your niche right now — with viral hooks ready to use." accent="from-cyan-500 to-blue-500">
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Input data-testid="trend-niche" placeholder="Niche (e.g. real estate, AI tools, fitness)" value={niche} onChange={(e) => setNiche(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
      </div>
      <RunButton onClick={run} loading={loading} testid="trend-run">Scan Trend Radar</RunButton>

      {data?.trends?.length > 0 && (
        <ResultBox>
          {data.trends.map((t) => (
            <div key={t.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-white">{t.title}</div>
                <Badge className="bg-cyan-500/15 border border-cyan-500/30 text-cyan-300"><TrendingUp className="w-3 h-3 mr-1" /> {t.heat_score}</Badge>
              </div>
              <div className="text-xs text-white/60 mb-1">Why now: {t.why_now}</div>
              <div className="text-xs text-white/70 mb-1">Your angle: {t.viral_angle}</div>
              <div className="text-xs text-orange-300 italic">Hook: "{t.sample_hook}"</div>
            </div>
          ))}
          <KFNStamp />
        </ResultBox>
      )}
    </ToolShell>
  );
};

// ============== Tool 8: Comment Killer ==============
const CommentKillerTool = () => {
  const [comment, setComment] = useState('');
  const [intent, setIntent] = useState('engage');
  const [useDna, setUseDna] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!comment.trim()) return toast.error('Paste the comment');
    setLoading(true); setData(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/comment-killer`,
        { comment, intent, use_brand_dna: useDna }, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <ToolShell icon={MessageCircleHeart} title="Comment Killer" subtitle="Paste any comment → 3 witty on-brand replies in YOUR voice. Save hours per week." accent="from-rose-500 to-pink-600">
      <Textarea data-testid="comment-input" placeholder="Paste the comment you want to reply to..." value={comment} onChange={(e) => setComment(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3" rows={3} />
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <select value={intent} onChange={(e) => setIntent(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md p-2.5">
          <option value="engage">Engage (friendly + spark conversation)</option>
          <option value="defuse">Defuse (handle hate gracefully)</option>
          <option value="convert">Convert (turn into a customer)</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-white/70 px-2">
          <input type="checkbox" checked={useDna} onChange={(e) => setUseDna(e.target.checked)} /> Match my Brand DNA
        </label>
      </div>
      <RunButton onClick={run} loading={loading} testid="comment-run">Generate Replies</RunButton>

      {data?.replies?.length > 0 && (
        <ResultBox>
          {data.replies.map((r) => (
            <div key={r.id} className={`rounded-lg border p-3 ${r.id === data.best_id ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`}>
              <div className="flex items-center justify-between mb-1">
                <Badge className="bg-pink-500/15 border border-pink-500/30 text-pink-300 text-[10px]">{r.style}</Badge>
                <div className="flex items-center gap-2">
                  {r.id === data.best_id && <Badge className="bg-emerald-500 text-white"><CheckCircle2 className="w-3 h-3 mr-1" /> Best</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => copyText(r.reply)} className="h-7 text-orange-300 hover:bg-orange-500/10"><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
              <div className="text-sm text-white/90">{r.reply}</div>
            </div>
          ))}
          <KFNStamp />
        </ResultBox>
      )}
    </ToolShell>
  );
};

export default AILab;
